import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Slider as AntSlider } from 'antd';
import debounce from 'lodash/debounce';
import Props from 'utils/proptypes';
import classNames from 'classnames';
import QuestionHeader from './QuestionHeader';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

class Slider extends Component {
  static propTypes = {
    text: PropTypes.string.isRequired,
    descriptor: PropTypes.string,
    id: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onIncomplete: PropTypes.func.isRequired,
    onAnswer: PropTypes.func.isRequired,
    required: PropTypes.bool.isRequired,
    answer: PropTypes.shape({
      value: PropTypes.number,
      type: Props.questionnaire.answerType,
    }),
    disabled: PropTypes.bool,
    isAnswerType: PropTypes.bool,
    readOnly: PropTypes.bool,
    extension: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.string.isRequired,
        valueString: PropTypes.string,
      }),
    ),
    flags: PropTypes.object,
    onNext: PropTypes.func,
  };

  static defaultProps = {
    answer: {},
    disabled: false,
    isAnswerType: false,
    readOnly: false,
    onNext: undefined,
  };

  state = {
    value: this.props.answer.value,
  };

  componentDidMount() {
    const { onAnswer, id, onComplete, required } = this.props;
    const { value } = this.state;
    if (typeof value === 'undefined') {
      onAnswer(id, { value: 0, valueType: 'integer' });
      if (!required) {
        onComplete();
      }
    }
  }

  componentWillUnmount() {
    this.debouncedNext.cancel();
  }

  parseSliderOptions() {
    const displayOptions = {
      slider: {
        min: null,
        max: null,
      },
    };

    try {
      if (this.props.extension && Array.isArray(this.props.extension)) {
        this.props.extension.forEach((ext) => {
          if (
            ext.url ===
            'http://webpt.com/fhir/StructureDefinition/questionnaireItemDisplaySliderMin'
          ) {
            displayOptions.slider.min = ext.valueString || '';
          }
          if (
            ext.url ===
            'http://webpt.com/fhir/StructureDefinition/questionnaireItemDisplaySliderMax'
          ) {
            displayOptions.slider.max = ext.valueString || '';
          }
        });
      }
    } catch (error) {
      console.error('Error parsing slider options:', error);
    }

    return displayOptions;
  }

  handleChange = (value) => {
    const { onAnswer, id, onComplete, onIncomplete, required } = this.props;
    onAnswer(id, { value, valueType: 'integer' });
    this.setState({
      value,
    });
    if (required && value === undefined) {
      onIncomplete();
    } else {
      onComplete();
    }
  };

  /** Keep local state aligned while dragging or using arrow keys (onAfterChange may lag). */
  handleValueChange = (value) => {
    this.setState({ value });
  };

  isEnterKey = (e) => e.key === 'Enter' || e.key === 'NumpadEnter';

  /**
   * antd/rc-slider does not treat Enter like radio groups; commit current value and advance
   * when `onNext` is provided (same flow as SingleChoice).
   */
  handleSliderKeyDownCapture = (e) => {
    if (!this.isEnterKey(e) || e.defaultPrevented) {
      return;
    }
    const { disabled, readOnly, onNext } = this.props;
    if (disabled || readOnly) {
      return;
    }
    const { target } = e;
    if (
      !target ||
      typeof target.closest !== 'function' ||
      !target.closest('.ant-slider')
    ) {
      return;
    }
    e.preventDefault();
    const raw = this.state.value;
    const value = raw === undefined || raw === null ? 0 : raw;
    this.handleChange(value);
    if (onNext) {
      this.handleGoNext();
    }
  };

  debouncedNext = debounce(() => {
    const { onNext } = this.props;
    if (onNext) {
      onNext();
    }
  }, 500);

  handleGoNext = () => {
    this.debouncedNext();
  };

  renderSlider = () => {
    const { disabled } = this.props;

    const displayOptions = this.parseSliderOptions();
    const min = displayOptions?.slider?.min;
    const max = displayOptions?.slider?.max;

    const sliderProps = {
      onChange: this.handleValueChange,
      onAfterChange: this.handleChange,
      defaultValue: this.state.value,
      min: 0,
      max: 10,
      step: 1,
      marks: { 0: 0, 5: 5, 10: 10 },
    };

    const mipsFlagEnabled = this.props.flags?.['ketKeet2519MipsEnhancements'];

    return (
      <div onKeyDownCapture={this.handleSliderKeyDownCapture}>
        <AntSlider {...sliderProps} disabled={disabled} />
        {mipsFlagEnabled && (min !== undefined || max !== undefined) && (
          <div className="flex flex-row items-center justify-between">
            <p>{min}</p>
            <p>{max}</p>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { text, descriptor, isAnswerType, readOnly } = this.props;
    return (
      <div
        data-testid="slider-question"
        className="ma3 w-80 center overflow-x-hidden"
      >
        <p
          className={classNames('b f5', isAnswerType ? 'tl' : 'tc')}
          style={{ overflowWrap: 'break-word' }}
        >
          <QuestionHeader
            linkId={this.props.id}
            text={text}
            descriptor={descriptor}
            isAnswerType={isAnswerType}
            readOnly={readOnly}
          />
        </p>
        <div className="md-cell--12 center">{this.renderSlider()}</div>
      </div>
    );
  }
}

export default withLDConsumer()(Slider);
